import React, {useMemo, useState} from "react"
import {useQuery} from "@apollo/client"
import {useHistory, useLocation} from "react-router-dom"

import movieListSingle from "graphql/Media/Movie/query/movieListSingle"
import {getImageUrlFromSizeAndPath, TmdbImageSizeEnum} from "helpers/Tmdb"
import {
  filterMoviesByGenre,
  getAvailableMovieGenreGroups,
  getMovieRating,
  getMovieYear,
  MovieGenreKey,
  MOVIE_GENRE_GROUP_LIST,
} from "helpers/Media/Movie/Genre"
import {URL_FRONT, URL_FRONT_MOVIE_PLAY} from "static/constant/url"

import "./style.scss"

type MovieSort = "TITLE" | "YEAR" | "RATING" | "ADDED"

const validGenreKeyList = MOVIE_GENRE_GROUP_LIST.map(group => group.key)

const MovieLibraryCard = ({movie}: {movie: any}) => {
  const history = useHistory()
  const posterPath = movie?.tmdbOptions?.poster_path
  const posterUrl = posterPath ? getImageUrlFromSizeAndPath(TmdbImageSizeEnum.W342, posterPath) : ""
  const year = getMovieYear(movie)
  const rating = getMovieRating(movie)
  const destination = movie?.isViewable ? URL_FRONT_MOVIE_PLAY.replace(":id", movie.id) : null

  return <button
    type="button"
    className={`movie-library-card ${destination ? "" : "is-unavailable"}`}
    onClick={() => destination && history.push(destination)}
    aria-label={destination ? `Ouvrir ${movie?.name}` : `${movie?.name}, bientôt disponible`}
  >
    <span
      className={`movie-library-card__poster ${posterUrl ? "has-image" : "no-image"}`}
      style={posterUrl ? {backgroundImage: `url(${posterUrl})`} : undefined}
    >
      {!posterUrl && <span className="fas fa-film" aria-hidden="true"></span>}
      <span className="movie-library-card__shade"></span>
      <span className="movie-library-card__control fas fa-play" aria-hidden="true"></span>
      {!destination && <span className="movie-library-card__availability">Bientôt</span>}
    </span>
    <span className="movie-library-card__copy">
      <strong>{movie?.name || "Sans titre"}</strong>
      <small>
        {year && <span>{year}</span>}
        {rating && <span><span className="fas fa-star" aria-hidden="true"></span> {rating.toFixed(1)}</span>}
      </small>
    </span>
  </button>
}

export const MoviesRoute = () => {
  const history = useHistory()
  const location = useLocation()
  const requestedGenre = new URLSearchParams(location.search).get("genre") as MovieGenreKey
  const initialGenre = validGenreKeyList.includes(requestedGenre) ? requestedGenre : "ALL"
  const [activeGenre, setActiveGenre] = useState<MovieGenreKey>(initialGenre)
  const [sort, setSort] = useState<MovieSort>("TITLE")
  const {data, loading} = useQuery(movieListSingle, {fetchPolicy: "cache-and-network"})
  const movieList = data?.movieList || []
  const genreGroupList = useMemo(() => getAvailableMovieGenreGroups(movieList), [movieList])

  const visibleMovieList = useMemo(() => {
    const filteredList = filterMoviesByGenre(movieList, activeGenre)
    return [...filteredList].sort((first, second) => {
      if (sort === "YEAR") return Number(getMovieYear(second) || 0) - Number(getMovieYear(first) || 0) || first.name.localeCompare(second.name)
      if (sort === "RATING") return Number(getMovieRating(second) || 0) - Number(getMovieRating(first) || 0) || first.name.localeCompare(second.name)
      if (sort === "ADDED") return new Date(second.additionDate || 0).getTime() - new Date(first.additionDate || 0).getTime() || first.name.localeCompare(second.name)
      return first.name.localeCompare(second.name)
    })
  }, [activeGenre, movieList, sort])

  const selectGenre = (genreKey: MovieGenreKey) => {
    setActiveGenre(genreKey)
    history.replace({
      pathname: location.pathname,
      search: genreKey === "ALL" ? "" : `?genre=${genreKey}`,
    })
  }

  return <main className="movies-route-container">
    <header className="movies-hero">
      <button type="button" className="movies-back-button" onClick={() => history.push(URL_FRONT)}>
        <span className="fas fa-arrow-left" aria-hidden="true"></span>
        <span>Accueil</span>
      </button>
      <span className="movies-eyebrow">Le catalogue cinéma</span>
      <h1>Tous les films</h1>
      <p>Parcourez la vidéothèque par univers, puis choisissez votre prochaine séance.</p>
    </header>

    <section className="movies-toolbar" aria-label="Filtres des films">
      <div className="movies-genre-list" role="group" aria-label="Filtrer par genre">
        {genreGroupList.map(group => <button
          key={group.key}
          type="button"
          className={activeGenre === group.key ? "is-active" : ""}
          onClick={() => selectGenre(group.key)}
          aria-pressed={activeGenre === group.key}
        >
          <span>{group.label}</span>
          <small>{group.movieCount}</small>
        </button>)}
      </div>
      <label className="movies-sort">
        <span>Trier par</span>
        <select value={sort} onChange={event => setSort(event.target.value as MovieSort)}>
          <option value="TITLE">Titre</option>
          <option value="ADDED">Date d’ajout</option>
          <option value="YEAR">Année de sortie</option>
          <option value="RATING">Note TMDB</option>
        </select>
      </label>
    </section>

    <div className="movies-results-heading">
      <h2>{genreGroupList.find(group => group.key === activeGenre)?.label || "Tous"}</h2>
      <span>{visibleMovieList.length} film{visibleMovieList.length > 1 ? "s" : ""}</span>
    </div>

    {loading && movieList.length === 0 && <div className="movies-grid movies-grid--loading" aria-label="Chargement des films">
      {Array.from({length: 10}, (_, index) => <span key={index}></span>)}
    </div>}

    {!loading && visibleMovieList.length === 0 && <div className="movies-empty-state">
      <span className="fas fa-film" aria-hidden="true"></span>
      <h2>Aucun film dans cette catégorie</h2>
      <p>Les films sans genre restent disponibles dans l’onglet Tous.</p>
    </div>}

    {visibleMovieList.length > 0 && <div className="movies-grid">
      {visibleMovieList.map(movie => <MovieLibraryCard key={movie.id} movie={movie} />)}
    </div>}
  </main>
}
